/**
 * Who a name looks like: its initials, and a tint taken from a continuous wheel rather than a shortlist.
 *
 * PORTED UNCHANGED FROM BirdJournal.SupportSite (`app/src/lib/avatar.ts`), and the point of copying it
 * verbatim is that the hash must agree: somebody who is a particular green on the ideas board is that
 * same green in this site's app bar. One person, one colour, across the estate. A "close enough"
 * reimplementation would produce a different colour for the same person, which is worse than having no
 * colour at all — so if this changes, change it in both, or extract it.
 *
 * The hash is over the WHOLE display name rather than the initials, even though the initials are what
 * shows. Two letters collide constantly — every J-something shares "J" — and the colour exists to tell
 * people apart, so hashing what is actually distinct is the point.
 */

/** How many depths of colour a hue can come in. See `tintOf`. */
const TONES = 3

export function initialsOf(name: string): string {
  // Split on punctuation as well as spaces: names are often "anne.m" or "john_smith", and on whitespace
  // alone those yield a single letter — the least distinguishing avatar there is.
  return name
    .split(/[\s._-]+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

/**
 * A hue anywhere on the wheel, and one of three depths at that hue.
 *
 * `tone` is what a wheel alone does not fix: two hashes landing 5° apart are still one colour to the
 * eye, so a second, independent axis is taken from the same hash. It comes from the quotient rather than
 * the remainder precisely so it does not track the hue.
 *
 * Only hue and chroma vary. LIGHTNESS IS FIXED IN THE THEME, which is what keeps one ink legible on
 * every result by construction rather than by having been checked.
 */
export function tintOf(name: string): { hue: number; tone: number } {
  let hash = 0x811c9dc5
  for (let i = 0; i < name.length; i++) {
    hash ^= name.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  const positive = Math.abs(hash)
  return { hue: positive % 360, tone: (Math.floor(positive / 360) % TONES) + 1 }
}

/**
 * What to call somebody with no display name. The account API may return null — an account that has
 * never set one — and the app bar still has to say something.
 *
 * The support site's equivalent is "A Bird Journal user", which is right there because it is shown to
 * OTHER people. Here it is only ever shown to the person themselves, in their own menu, above their own
 * email address, so the second person reads better and the email identifies them regardless.
 */
export const YOUR_ACCOUNT_LABEL = 'Your account'
